import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ActionProposalCard } from '../../SuperAdmin/ActionProposalCard.jsx';
import { DealerReportCard } from '../../Dealer/DealerReportCard.jsx';

/**
 * MarkdownText — render markdown 字串。
 *   - GFM (table, strikethrough, task list)
 *   - 程式碼 fences 用 Prism 高亮（淺色主題搭 admin-console cream 風）
 *   - inline code 用 mono + 灰底
 *   - 列表、表格、blockquote 都收斂在 cream/ink palette 風格
 */
// AI streaming markdown 的常見坑：
// 1) **A****B** — 多 section 沒分隔，4 個 * 連黏 → 拆成兩段 bold
// 2) `** X **` — bold delimiter 跟內容之間多空白，markdown 不認 → trim 成 `**X**`
//
// 註：原本想處理 CJK 邊界（**X**中文），但 regex 無法分辨 ** 是 open 還是 close，
// 結果把 close 後的 ** 誤判成新 open，加空格反而拆壞配對。砍掉，靠 react-markdown +
// remark-gfm 自己處理（GFM 對 CJK 邊界其實大多 OK，主要問題是空白）
function normalizeMd(text) {
    if (!text) return '';
    let s = String(text);

    // 0. 剝掉「把 fenced block 再包一層 4+ 反引號」的外層（AI 照抄 skill 文件裡的
    //    escaped 範例時會這樣輸出）— 只在內層真的含 ``` fence 時才剝，避免誤傷
    s = s.replace(/`{4,}[^\S\n]*\n([\s\S]*?)\n`{4,}/g, (m, inner) => (inner.includes('```') ? inner : m));

    // 1. 先把 fenced code 區塊抽出 placeholder — 下面的 markdown 修補規則（**、[] escape）
    //    不可以動到 code 內容（曾把 JSON 裡的 [ ] 加上反斜線、**** 拆行，整個 fence 解析爆掉）
    const fences = [];
    s = s.replace(/```[\s\S]*?```/g, (m) => {
        fences.push(m);
        return `\n@@QBFENCE${fences.length - 1}@@\n`;
    });

    s = s
        // 2. 拆 ****（兩個 bold 黏在一起）
        .replace(/\*{4,}/g, '**\n\n**')
        // 3. **X**** → **X**\n\n** （保險）
        .replace(/\*\*(?=\S)([^*\n]+?)(?<=\S)\*\*(\*\*)/g, '**$1**\n\n$2')
        // 4. 吃掉 ** 跟內容之間的空白（半形 / tab / 全形空格）
        .replace(/\*\*[ \t　]+([^\n*]+?)[ \t　]*\*\*/g, '**$1**')
        .replace(/\*\*[ \t　]*([^\n*]+?)[ \t　]+\*\*/g, '**$1**')
        // 5. Escape 非 link 的 [text]
        //    AI 講「ivrFlowConfig.nodes[router]」時 markdown 會把 [router] 當 link reference 解析，
        //    破壞整段 inline 結構（連帶把外圍 **X** bold 也解不出來）。判斷規則：
        //    若 [text] 後面沒接 `(`(inline link)、`:`(reference def)、`[`(ref link) → escape
        .replace(/\[([^\]\n]+)\](?![(:\[])/g, '\\[$1\\]')
        // 6. close ** 前是中文標點時補空格
        //    CommonMark right-flanking 規則：close ** 前是 punctuation + 後是字母 → 不算 close
        .replace(/([　-〿＀-￯])\*\*([^\s*])/g, '$1** $2');

    // 還原 fenced code
    s = s.replace(/@@QBFENCE(\d+)@@/g, (_, i) => fences[Number(i)]);
    return s;
}

// AI 會用 <details><summary>技術明細</summary>…</details> 收合技術細節，但 react-markdown
// 沒開 raw HTML（XSS 考量）會把標籤顯示成文字 → 自己拆出來用原生 <details> 渲染，
// 內文（常含 code fence）遞迴走 markdown。
function splitDetails(text) {
    const s = String(text || '');
    const re = /<details>\s*(?:<summary>([\s\S]*?)<\/summary>)?([\s\S]*?)<\/details>/g;
    const parts = [];
    let last = 0;
    let m;
    while ((m = re.exec(s))) {
        if (m.index > last) parts.push({ md: s.slice(last, m.index) });
        parts.push({ summary: (m[1] || '').trim() || '詳細資訊', body: (m[2] || '').trim() });
        last = m.index + m[0].length;
    }
    if (last < s.length) parts.push({ md: s.slice(last) });
    return parts;
}

export function MarkdownText({ children, className = '' }) {
    const parts = splitDetails(children);
    if (parts.length > 1 || (parts[0] && parts[0].summary !== undefined)) {
        return (
            <div className={className}>
                {parts.map((p, i) => (p.md !== undefined
                    ? <MarkdownText key={i}>{p.md}</MarkdownText>
                    : (
                        <details key={i} className="my-2 rounded-md border border-cream-200 bg-cream-50/60 px-3 py-1.5">
                            <summary className="cursor-pointer select-none text-xs font-medium text-ink-500 hover:text-ink-700">
                                {p.summary}
                            </summary>
                            <div className="pt-1.5"><MarkdownText>{p.body}</MarkdownText></div>
                        </details>
                    )))}
            </div>
        );
    }
    return (
        <div className={['prose prose-sm max-w-none', className].join(' ')}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    // action-proposal 卡片不能活在 <pre> 裡（white-space:pre 會讓卡片內文全部不換行）
                    pre({ node, children, ...props }) {
                        const cls = node?.children?.[0]?.properties?.className || [];
                        if (Array.isArray(cls) && (cls.includes('language-action-proposal') || cls.includes('language-dealer-report'))) {
                            return <>{children}</>;
                        }
                        return <pre {...props}>{children}</pre>;
                    },
                    code({ inline, className, children, ...props }) {
                        const match = /language-([\w-]+)/.exec(className || '');
                        // 超管 AI 提案卡：```action-proposal fenced JSON → 確認執行卡片
                        if (!inline && match && match[1] === 'action-proposal') {
                            return <ActionProposalCard raw={String(children)} />;
                        }
                        // 經銷商「回報給 Qubby」卡：```dealer-report fenced JSON → 確認回報按鈕
                        if (!inline && match && match[1] === 'dealer-report') {
                            return <DealerReportCard raw={String(children)} />;
                        }
                        if (!inline && match) {
                            return (
                                <SyntaxHighlighter
                                    language={match[1]}
                                    style={oneLight}
                                    customStyle={{
                                        margin: '0.5em 0',
                                        padding: '0.75em',
                                        background: '#fbf8f1',
                                        border: '1px solid #efe6d4',
                                        borderRadius: '6px',
                                        fontSize: '12.5px',
                                    }}
                                    {...props}
                                >
                                    {String(children).replace(/\n$/, '')}
                                </SyntaxHighlighter>
                            );
                        }
                        return (
                            <code className="px-1 py-0.5 rounded bg-cream-100 text-ink-700 font-mono text-[12.5px]" {...props}>
                                {children}
                            </code>
                        );
                    },
                    p: ({ children }) => <p className="my-2 text-ink-900 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="my-2 ml-5 list-disc text-ink-900">{children}</ul>,
                    ol: ({ children }) => <ol className="my-2 ml-5 list-decimal text-ink-900">{children}</ol>,
                    li: ({ children }) => <li className="my-0.5">{children}</li>,
                    h1: ({ children }) => <h1 className="heading-display text-lg font-semibold text-ink-900 mt-3 mb-1">{children}</h1>,
                    h2: ({ children }) => <h2 className="heading-display text-base font-semibold text-ink-900 mt-3 mb-1">{children}</h2>,
                    h3: ({ children }) => <h3 className="heading-display text-sm font-semibold text-ink-900 mt-2 mb-1">{children}</h3>,
                    blockquote: ({ children }) => (
                        <blockquote className="border-l-2 border-accent-300 pl-3 my-2 text-ink-700 italic">{children}</blockquote>
                    ),
                    table: ({ children }) => (
                        <div className="overflow-x-auto my-2"><table className="text-sm border-collapse">{children}</table></div>
                    ),
                    th: ({ children }) => <th className="border border-cream-300 px-2 py-1 bg-cream-100 text-left font-semibold">{children}</th>,
                    td: ({ children }) => <td className="border border-cream-300 px-2 py-1">{children}</td>,
                    a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener" className="text-accent-700 hover:underline">{children}</a>
                    ),
                }}
            >
                {normalizeMd(children)}
            </ReactMarkdown>
        </div>
    );
}
